import { useState, useEffect, useCallback } from 'react';

interface UseRedirectionModalReturn {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  confirmRedirection: (url: string) => void;
  handleContinue: () => void;
  pendingUrl: string | null;
  countdown: number;
}

export const useRedirectionModal = (): UseRedirectionModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setPendingUrl(null);
    setCountdown(3);
  }, []);

  const handleContinue = useCallback(() => {
    if (pendingUrl) {
      window.open(pendingUrl, '_blank', 'noopener,noreferrer');
    }
    closeModal();
  }, [pendingUrl, closeModal]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isOpen && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (isOpen && countdown === 0) {
      handleContinue();
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isOpen, countdown, handleContinue]);

  const openModal = useCallback(() => {
    setCountdown(3);
    setIsOpen(true);
  }, []);

  const confirmRedirection = useCallback((url: string) => {
    setPendingUrl(url);
    setCountdown(3);
    setIsOpen(true);
  }, []);

  return {
    isOpen,
    openModal,
    closeModal,
    confirmRedirection,
    handleContinue,
    pendingUrl,
    countdown,
  };
};
